import dayjs from 'dayjs';
import { useState } from 'react';
import { useEffect } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import Alert from '../../../components/ui/Alert';
import Card from '../../../components/ui/Card';
import { useOrder } from '../../../lib/hooks/use-orders';
import AcceptOrder from '../../../components/orders/AcceptOrder';
import RejectOrder from '../../../components/orders/RejectOrder';
import FulfillOrder from '../../../components/orders/FulfillOrder';
import Loader from '../../../components/ui/Loader';
import Back from '../../../components/ui/Back';
import Edit from '../../../components/ui/Edit';
import Button from '../../../components/ui/Button';
import { useCheckAccess } from '../../../lib/auth/use-checkAccess';
import Content from '../../../components/layout/Content';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faComment,
  faUserCircle,
  faClock,
  faCheckCircle,
  faTimesCircle,
  faTrashAlt,
} from '@fortawesome/free-regular-svg-icons';
import { faTruck } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../../lib/hooks/use-auth';
import CancelOrder from '../../../components/orders/CancelOrder';

type OrderDetailsParams = 'id';

const Order = () => {
  const { id } = useParams<OrderDetailsParams>();
  const { id: userId } = useAuth();
  const checkAccess = useCheckAccess();
  const navigate = useNavigate();
  const { order, error, update, loading } = useOrder(id);
  const [showAccept, setShowAccept] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [showFulfill, setShowFulfill] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showFulfillPartially, setShowFulfillPartially] = useState(false);
  const [showFulfillTier2, setShowFulfillTier2] = useState(false);
  const [displayEditButton, setDisplayEditButton] = useState(false);
  const [displayComment, setDisplayComment] = useState(false);
  const [displayAcceptButton, setDisplayAcceptButton] = useState(false);
  const [displayRejectButton, setDisplayRejectButton] = useState(false);
  const [displayFulfillButton, setDisplayFulfillButton] = useState(false);
  const [displayCancelButton, setDisplayCancelButton] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  useEffect(() => {
    if (error) {
      setShowAlert(true);
    } else if (showAlert) {
      setShowAlert(false);
    }
    return () => {
      setShowAlert(false);
    };
  }, [error]);

  useEffect(() => {
    if (order) {
      switch (order.status) {
        case 'pending':
          setDisplayEditButton(true);
          setDisplayAcceptButton(true);
          setDisplayRejectButton(true);
          setDisplayCancelButton(order.createdBy.id === userId);
          break;
        case 'accepted':
          setDisplayFulfillButton(true);
          setDisplayComment(true);

          break;
        case 'fulfilled':
          setDisplayComment(true);

          break;
        case 'rejected':
          setDisplayComment(true);

          break;
        default:
          break;
      }
    }
  }, [order]);

  const handleAccept = async (data) => {
    await update({ status: 'accepted', comment: data.comment });
    navigate('/orders', { replace: true });
  };
  const handleReject = async (data) => {
    await update({ status: 'rejected', comment: data.comment });
    navigate('/orders', { replace: true });
  };
  const handleCancel = async (data) => {
    await update({ status: 'canceled', comment: data.comment });
    navigate('/orders', { replace: true });
  };
  const handleFulfill = async (data) => {
    const fulfillments = order.fulfillments ? order.fulfillments + 1 : 1;

    const classic = isNaN(data.classic) ? 0 : data.classic;
    const supaset = isNaN(data.supaset) ? 0 : data.supaset;
    const supafixc0 = isNaN(data.supafixc0) ? 0 : data.supafixc0;
    const supafixc1 = isNaN(data.supafixc1) ? 0 : data.supafixc1;

    const productsDelivered = {
      classic:
        order.productsDelivered && order.productsDelivered.classic
          ? order.productsDelivered.classic + classic
          : classic,
      supaset:
        order.productsDelivered && order.productsDelivered.supaset
          ? order.productsDelivered.supaset + supaset
          : supaset,
      supafixc0:
        order.productsDelivered && order.productsDelivered.supafixc0
          ? order.productsDelivered.supafixc0 + supafixc0
          : supafixc0,
      supafixc1:
        order.productsDelivered && order.productsDelivered.supafixc1
          ? order.productsDelivered.supafixc1 + supafixc1
          : supafixc1,
    };
    await update({
      status: 'fulfilled',
      productsDelivered,
      fulfillments: fulfillments,
    });
    navigate('/orders', { replace: true });
  };

  const handleFulfillPartially = async (data) => {
    const classic = isNaN(data.classic) ? 0 : data.classic;
    const supaset = isNaN(data.supaset) ? 0 : data.supaset;
    const supafixc0 = isNaN(data.supafixc0) ? 0 : data.supafixc0;
    const supafixc1 = isNaN(data.supafixc1) ? 0 : data.supafixc1;

    const productsDelivered = {
      classic:
        order.productsDelivered && order.productsDelivered.classic
          ? order.productsDelivered.classic + classic
          : classic,
      supaset:
        order.productsDelivered && order.productsDelivered.supaset
          ? order.productsDelivered.supaset + supaset
          : supaset,
      supafixc0:
        order.productsDelivered && order.productsDelivered.supafixc0
          ? order.productsDelivered.supafixc0 + supafixc0
          : supafixc0,
      supafixc1:
        order.productsDelivered && order.productsDelivered.supafixc1
          ? order.productsDelivered.supafixc1 + supafixc1
          : supafixc1,
    };

    const fulfillments = order.fulfillments ? order.fulfillments + 1 : 1;

    await update({
      productsDelivered,
      fulfillments: fulfillments,
    });
    navigate('/orders', { replace: true });
  };

  const handleFulfillPartiallyTier2 = async (data) => {
    const classic = isNaN(data.classic) ? 0 : data.classic;
    const supaset = isNaN(data.supaset) ? 0 : data.supaset;
    const supafixc0 = isNaN(data.supafixc0) ? 0 : data.supafixc0;
    const supafixc1 = isNaN(data.supafixc1) ? 0 : data.supafixc1;
    const productsDelivered = {
      classic:
        order.productsDelivered && order.productsDelivered.classic
          ? order.productsDelivered.classic + classic
          : classic,
      supaset:
        order.productsDelivered && order.productsDelivered.supaset
          ? order.productsDelivered.supaset + supaset
          : supaset,
      supafixc0:
        order.productsDelivered && order.productsDelivered.supafixc0
          ? order.productsDelivered.supafixc0 + supafixc0
          : supafixc0,
      supafixc1:
        order.productsDelivered && order.productsDelivered.supafixc1
          ? order.productsDelivered.supafixc1 + supafixc1
          : supafixc1,
    };

    await update({
      productsDelivered,
    });
  };

  return (
    <>
      {checkAccess(['sop', 'distributor']) && (
        <AcceptOrder
          open={showAccept}
          onAccept={handleAccept}
          setOpen={(open) => setShowAccept(open)}
        />
      )}
      {checkAccess(['sop', 'distributor']) && (
        <RejectOrder
          open={showReject}
          onReject={handleReject}
          setOpen={(open) => setShowReject(open)}
        />
      )}
      {checkAccess(['sop', 'distributor', 'fsa']) && (
        <CancelOrder
          open={showCancel}
          onCancel={handleCancel}
          setOpen={(open) => setShowCancel(open)}
        />
      )}
      {checkAccess(['fsa', 'sop', 'distributor']) && (
        <FulfillOrder
          open={showFulfill}
          order={order}
          onFulfill={handleFulfill}
          setOpen={(open) => setShowFulfill(open)}
          confirmText="Are you sure you want to fulfill the order?"
          buttonText="Fulfill"
        />
      )}
      {checkAccess(['fsa', 'sop', 'distributor']) && (
        <FulfillOrder
          open={showFulfillPartially}
          order={order}
          onFulfill={handleFulfillPartially}
          setOpen={(open) => setShowFulfillPartially(open)}
          confirmText="Are you sure you want to partially fulfill the order?"
          buttonText="Partially fulfill"
        />
      )}
      {checkAccess(['fsa', 'sop', 'distributor']) && (
        <FulfillOrder
          open={showFulfillTier2}
          order={order}
          onFulfillTier2={handleFulfillPartiallyTier2}
          tier2={true}
          setOpen={(open) => setShowFulfillTier2(open)}
          confirmText="Are you sure you want to fulfill this tier 2 order?"
          buttonText="Fulfil to tier 2 outlet"
        />
      )}
      <Loader show={loading} />
      <Alert
        message={error && error.message}
        open={showAlert}
        setOpen={(open) => setShowAlert(open)}
        title="Error"
      />
      <Content>
        {loading || order ? (
          <Card className="grid grid-cols-10">
            <Back to="/orders" className="col-span-3" />
            <div className="col-span-4 flex justify-center items-center text-center text-xl font-bold text-lh-head-black">
              {order && (
                <FontAwesomeIcon
                  icon={
                    order.createdOn === 'chatbot' ? faComment : faUserCircle
                  }
                  className="mr-2 text-lh-head-black"
                />
              )}
              {order && order.id}
            </div>
            {displayEditButton && (
              <Edit className="col-span-3 flex-row-reverse" />
            )}
            <h2 className="col-span-8 font-bold">
              {order && order.outlet.name}
              <p className="col-span-full font-normal text-lh-text-black">
                {order && dayjs(order.createdAt).format('DD.MM.YYYY HH:mm')}
              </p>
            </h2>
            <div className="col-span-2 flex flex-col items-center font-bold text-lg text-lh-head-black">
              <p>Status</p>
              {order &&
                order.status &&
                (order.status === 'pending' ? (
                  <FontAwesomeIcon icon={faClock} className="text-2xl mt-0" />
                ) : order.status === 'accepted' ? (
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    className="text-2xl mt-0"
                  />
                ) : order.status === 'rejected' ? (
                  <FontAwesomeIcon
                    icon={faTimesCircle}
                    className="text-2xl mt-0"
                  />
                ) : order.status === 'fulfilled' ? (
                  <FontAwesomeIcon icon={faTruck} className="text-2xl mt-0" />
                ) : order.status === 'canceled' ? (
                  <FontAwesomeIcon
                    icon={faTrashAlt}
                    className="text-2xl mt-0"
                  />
                ) : (
                  ''
                ))}
            </div>
            <hr className="col-span-full mt-2 mb-2" />
            <h2 className="col-span-full font-bold text-lh-head-black">FSA</h2>
            <p className="col-span-full text-lh-text-black">
              {order && order.fsa.name}
            </p>
            <h2 className="col-span-full font-bold text-lh-head-black">
              Distributor
            </h2>
            <p className="col-span-full text-lh-text-black">
              {order && order.distributor.name}
            </p>
            <h2 className="col-span-full font-bold mt-4 mb-2 text-lh-head-black">
              {order &&
                (['accepted', 'fulfilled'].includes(order.status)
                  ? 'Products fulfilled / ordered'
                  : 'Products ordered')}
            </h2>
            <h3 className="col-span-2 py-1 text-lh-text-black">Classic</h3>
            <p className="col-span-3 col-start-7 mb-4 text-right py-1 px-3 font-semibold text-lh-text-black">
              {order &&
                (['accepted', 'fulfilled'].includes(order.status)
                  ? (order.productsDelivered
                      ? order.productsDelivered.classic
                      : 0) +
                    '/' +
                    order.products.classic
                  : order.products.classic)}
            </p>
            <p className="col-span-1 py-1 text-gray-500 font-thin">bags</p>
            <h3 className="col-span-2 py-1 text-lh-text-black">Supaset</h3>
            <p className="col-span-3 col-start-7 mb-4 text-right py-1 px-3 font-semibold text-lh-text-black">
              {order &&
                (['accepted', 'fulfilled'].includes(order.status)
                  ? (order.productsDelivered
                      ? order.productsDelivered.supaset
                      : 0) +
                    '/' +
                    order.products.supaset
                  : order.products.supaset)}
            </p>
            <p className="col-span-1 py-1 text-gray-500 font-thin">bags</p>
            <h3 className="col-span-2 py-1 text-lh-text-black">SupafixC0</h3>
            <p className="col-span-3 col-start-7 mb-4 text-right py-1 px-3 font-semibold text-lh-text-black">
              {order &&
                (['accepted', 'fulfilled'].includes(order.status)
                  ? (order.productsDelivered
                      ? order.productsDelivered.supafixc0
                      : 0) +
                    '/' +
                    order.products.supafixc0
                  : order.products.supafixc0)}
            </p>
            <p className="col-span-1 py-1 text-gray-500 font-thin">bags</p>
            <h3 className="col-span-2 py-1 text-lh-text-black">SupafixC1</h3>
            <p className="col-span-3 col-start-7 mb-4 text-right py-1 px-3 font-semibold text-lh-text-black">
              {order &&
                (['accepted', 'fulfilled'].includes(order.status)
                  ? (order.productsDelivered
                      ? order.productsDelivered.supafixc1
                      : 0) +
                    '/' +
                    order.products.supafixc1
                  : order.products.supafixc1)}
            </p>
            <p className="col-span-1 py-1 text-gray-500 font-thin">bags</p>
            <h2 className="col-span-full font-bold mb-2 text-lh-head-black">
              Delivery Address
            </h2>
            <p className="col-span-full rounded-lg mb-4 py-1 px-3 border-2 text-lh-text-black">
              {order && order.address}
            </p>
            {displayComment && (
              <>
                <h2 className="col-span-full font-bold mb-2 text-lh-head-black">
                  Comment
                </h2>
                <p className="col-span-full rounded-lg mb-4 py-1 px-3 border-2 text-lh-text-black">
                  {order && order.comment}
                </p>
              </>
            )}

            {checkAccess(['sop', 'distributor']) && displayAcceptButton && (
              <Button
                onClick={() => setShowAccept(true)}
                color="green"
                type="button"
                className="col-span-full mt-2 py-2"
              >
                Accept
              </Button>
            )}
            {checkAccess(['sop', 'distributor']) &&
              displayRejectButton &&
              !displayCancelButton && (
                <Button
                  onClick={() => setShowReject(true)}
                  color="red"
                  type="button"
                  className="col-span-full mt-2 py-2"
                >
                  Reject
                </Button>
              )}
            {checkAccess(['sop', 'fsa', 'distributor']) && displayCancelButton && (
              <Button
                onClick={() => setShowCancel(true)}
                color="red"
                type="button"
                className="col-span-full mt-2 py-2"
              >
                Cancel
              </Button>
            )}
            {checkAccess(['fsa', 'sop', 'distributor']) &&
              displayFulfillButton && (
                <Button
                  onClick={() => setShowFulfill(true)}
                  color="blue"
                  type="button"
                  className="col-span-full mt-2 py-2"
                >
                  Fulfil
                </Button>
              )}
            {checkAccess(['fsa', 'sop', 'distributor']) &&
              displayFulfillButton && (
                <Button
                  onClick={() => setShowFulfillPartially(true)}
                  color="blue"
                  type="button"
                  className="col-span-5 mt-2 py-2"
                >
                  Partially fulfil
                </Button>
              )}
            {checkAccess(['fsa', 'sop', 'distributor']) &&
              displayFulfillButton && (
                <Button
                  onClick={() => setShowFulfillTier2(true)}
                  color="blue"
                  type="button"
                  className="col-span-5 mt-2 py-2"
                >
                  Fulfil to tier 2 outlet
                </Button>
              )}
          </Card>
        ) : (
          <Card>Order not found!</Card>
        )}
      </Content>
    </>
  );
};

export default Order;
